import React from "react";
import { DownOutlined } from "@ant-design/icons";
import { Input } from "antd";
import dayjs from "dayjs";
import { AppDatePicker, AppSelect, CustomEntrySelect } from "@/shared/components";
import { Icon } from "@/shared/components/Icon/Icon";
import { formatBudgetValue } from "@/shared/utils/budget";
import { CURRENCY_OPTIONS } from "@/shared/utils/currency";
import { PAYMENT_TERM_OPTIONS } from "./constants";
import {
	Field,
	FieldLabelRow,
	Fields,
	InlineError,
	RequiredMark,
	SectionCopy,
	SectionDescription,
	SectionHeader,
	SectionTitle,
	SectionTitleRow,
	TermsSection,
	TermsStatusBadge,
} from "./SectionPrimitives";
import type {
	EngagementTermsFormController,
	EngagementTermsSectionStatusProps,
	RegisterValidationTarget,
} from "./types";

interface EffectiveDatesSectionProps extends EngagementTermsSectionStatusProps {
	form: EngagementTermsFormController;
	locked: boolean;
	registerValidationTarget: RegisterValidationTarget;
}

export const EffectiveDatesSection: React.FC<EffectiveDatesSectionProps> = ({
	form,
	locked,
	showStatusBadges,
	customizedFields,
	registerValidationTarget,
}) => (
	<TermsSection>
		<SectionHeader>
			<Icon src="/icon/cet-effective-date.svg" size={24} preserveOriginalColor />
			<SectionCopy>
				<SectionTitleRow>
					<SectionTitle>Effective Dates</SectionTitle>
					{showStatusBadges && (
						<TermsStatusBadge field="effectiveDates" customizedFields={customizedFields} />
					)}
				</SectionTitleRow>
				<SectionDescription>When this agreement is active for the creators.</SectionDescription>
			</SectionCopy>
		</SectionHeader>
		<Fields>
			<Field ref={registerValidationTarget("startDate")} $invalid={!!form.validationErrors.startDate}>
				<span>
					Effective Start Date<RequiredMark aria-hidden="true">*</RequiredMark>
				</span>
				<AppDatePicker
					value={form.startDate ? dayjs(form.startDate) : null}
					format="MMM D, YYYY"
					suffixIcon={
						locked ? undefined : <Icon src="/icon/cet-calendar.svg" size={20} preserveOriginalColor />
					}
					onChange={(date) => {
						form.setStartDate(date?.format("YYYY-MM-DD") || "");
						form.clearValidationError("startDate");
					}}
				/>
				{form.validationErrors.startDate && <InlineError>{form.validationErrors.startDate}</InlineError>}
			</Field>
			<Field ref={registerValidationTarget("endDate")} $invalid={!!form.validationErrors.endDate}>
				<span>
					Effective End Date<RequiredMark aria-hidden="true">*</RequiredMark>
				</span>
				<AppDatePicker
					value={form.endDate ? dayjs(form.endDate) : null}
					format="MMM D, YYYY"
					disabledDate={(date) => !!form.startDate && date.isBefore(dayjs(form.startDate), "day")}
					suffixIcon={
						locked ? undefined : <Icon src="/icon/cet-calendar.svg" size={20} preserveOriginalColor />
					}
					onChange={(date) => {
						form.setEndDate(date?.format("YYYY-MM-DD") || "");
						form.clearValidationError("endDate");
					}}
				/>
				{form.validationErrors.endDate && <InlineError>{form.validationErrors.endDate}</InlineError>}
			</Field>
		</Fields>
	</TermsSection>
);

interface CreatorFeeSectionProps extends EngagementTermsSectionStatusProps {
	form: EngagementTermsFormController;
	isCreatorMode: boolean;
	registerValidationTarget: RegisterValidationTarget;
}

export const CreatorFeeSection: React.FC<CreatorFeeSectionProps> = ({
	form,
	isCreatorMode,
	showStatusBadges,
	customizedFields,
	registerValidationTarget,
}) => (
	<TermsSection>
		<SectionHeader>
			<Icon src="/icon/cet-fee.svg" size={24} preserveOriginalColor />
			<SectionCopy>
				<SectionTitle>Creator Fee</SectionTitle>
			</SectionCopy>
		</SectionHeader>
		<Fields $columns={isCreatorMode ? 3 : 2}>
			{isCreatorMode && (
				<Field ref={registerValidationTarget("amount")} $invalid={!!form.validationErrors.amount}>
					<FieldLabelRow>
						Amount<RequiredMark aria-hidden="true">*</RequiredMark>
					</FieldLabelRow>
					<Input
						value={form.amount}
						inputMode="decimal"
						placeholder="Enter amount"
						onChange={(event) => {
							form.setAmount(formatBudgetValue(event.target.value));
							form.clearValidationError("amount");
						}}
					/>
					{form.validationErrors.amount && <InlineError>{form.validationErrors.amount}</InlineError>}
				</Field>
			)}
			<Field ref={registerValidationTarget("currency")} $invalid={!!form.validationErrors.currency}>
				<FieldLabelRow>
					Currency<RequiredMark aria-hidden="true">*</RequiredMark>
					{showStatusBadges && (
						<TermsStatusBadge field="currency" customizedFields={customizedFields} compact />
					)}
				</FieldLabelRow>
				<AppSelect
					value={form.currency || undefined}
					placeholder="Select currency"
					showSearch
					optionFilterProp="value"
					onChange={(value) => {
						form.setCurrency(value);
						form.clearValidationError("currency");
					}}
					options={CURRENCY_OPTIONS}
					suffixIcon={<DownOutlined />}
				/>
				{form.validationErrors.currency && <InlineError>{form.validationErrors.currency}</InlineError>}
			</Field>
			<Field ref={registerValidationTarget("paymentTerms")} $invalid={!!form.validationErrors.paymentTerms}>
				<FieldLabelRow>
					Payment Terms<RequiredMark aria-hidden="true">*</RequiredMark>
					{showStatusBadges && (
						<TermsStatusBadge field="paymentTerms" customizedFields={customizedFields} compact />
					)}
				</FieldLabelRow>
				<CustomEntrySelect
					value={form.paymentTerms || undefined}
					placeholder="Select payment terms"
					onChange={(value) => {
						form.setPaymentTerms(value);
						form.clearValidationError("paymentTerms");
					}}
					options={PAYMENT_TERM_OPTIONS.map((value) => ({ value, label: value }))}
					suffixIcon={<DownOutlined />}
				/>
				{form.validationErrors.paymentTerms && <InlineError>{form.validationErrors.paymentTerms}</InlineError>}
			</Field>
		</Fields>
	</TermsSection>
);
